import React, { useState, useEffect, useRef } from "react";
import {
  Bell,
  HelpCircle,
  Settings,
  User,
  Search,
  Plus,
  Edit,
  Save,
  X,
  BookOpen,
  Calendar,
  ClipboardList,
  Bot,
  Home,
  Users,
  ChevronRight,
  CalendarClock,
  ListTodo,
  CalendarDays,
  ClipboardCheck,
  Video,
  Megaphone,
  FolderOpen,
  FileSpreadsheet,
  PenTool,
  Check,
  Loader,
  Info,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Hourglass,
  Sparkles,
  PanelsTopLeft,
  Compass,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import checkPermission from "../utils/permission";
import { useTheme } from "../contexts/ThemeContext";
import { ApiService } from "../services/api";
import {
  ACTIVE_FOCUS_SESSION_EVENT,
  clearActiveFocusSessionSummary,
  formatSeconds,
  getLiveFocusSessionSnapshot,
  normalizeActiveFocusSessionSummary,
  readActiveFocusSessionSummary,
  storeActiveFocusSessionSummary,
} from "../utils/timeTracking";

const searchableItems = [
  {
    id: "dashboard",
    title: "Ana Sayfa",
    icon: Home,
    color: "#2563eb",
    path: "/dashboard",
    keywords: ["ana sayfa", "genel bakış", "merkez", "başlangıç"],
  },
  {
    id: "announcements",
    title: "Duyurular",
    icon: Megaphone,
    color: "#ca8a04",
    path: "/announcements",
    keywords: [
      "duyurular",
      "duyuru",
      "yeni duyuru",
      "duyuru ekle",
      "duyuru oluştur",
      "duyuru düzenle",
      "duyuru sil",
      "duyuru listesi",
      "tüm duyurular",
      "duyuru başlığı",
      "duyuru mesajı",
      "markdown",
      "departman seçimi",
      "ana sayfada görünür",
      "duyuru önizleme",
    ],
  },
  {
    id: "employees",
    title: "Üyeler",
    icon: Users,
    color: "#0f766e",
    path: "/employees",
    keywords: [
      "üyeler",
      "kullanıcılar",
      "personel",
      "departman",
      "yeni kullanıcı",
      "kullanıcı ekle",
      "kullanıcı düzenle",
      "kullanıcı sil",
      "yeni departman",
      "departman ekle",
      "departman düzenle",
      "departman sil",
      "departman izinleri",
      "izinler",
      "kullanıcı listesi",
      "departman listesi",
    ],
  },
  {
    id: "calendar",
    title: "Takvim",
    icon: Calendar,
    color: "#c2410c",
    path: "/calendar",
    keywords: [
      "takvim",
      "etkinlik",
      "randevu",
      "tarih",
      "gün",
      "ay",
      "yıl",
      "planlama",
      "program",
      "zamanlama",
      "etkinlik ekle",
      "yeni etkinlik",
      "etkinlik düzenle",
      "etkinlik sil",
      "günlük görünüm",
      "haftalık görünüm",
      "aylık görünüm",
    ],
  },
  {
    id: "navigator",
    title: "Koçum",
    icon: Compass,
    color: "#2563eb",
    path: "/navigator",
    keywords: [
      "koçum",
      "kocum",
      "koç",
      "navigatör",
      "durumum",
      "seviye tespiti",
      "seviyeni ölç",
      "bana dersi anlat",
      "kazanım",
      "ilerleme",
      "defterim",
      "günlük",
      "öğrenme defteri",
    ],
  },
  {
    id: "lessons",
    title: "E-Eğitim",
    icon: BookOpen,
    color: "#be185d",
    path: "/lessons",
    keywords: [
      "dersler",
      "eğitim",
      "kurs",
      "ders listesi",
      "derslerim",
      "yeni ders",
      "ders ekle",
      "ders düzenle",
      "ders sil",
      "ders detayı",
      "ders içeriği",
      "ders oynat",
      "video",
      "video oynat",
      "ders bölümü",
      "bölüm ekle",
      "bölüm sil",
      "ders notları",
      "ders yorumları",
      "ders soruları",
      "ders ödevleri",
      "ders raporları",
      "akademik takvim",
      "ders ayarları",
    ],
  },
  {
    id: "surveys",
    title: "Anketler",
    icon: ClipboardCheck,
    color: "#dc2626",
    path: "/surveys",
    keywords: [
      "anketler",
      "değerlendirme",
      "form",
      "soru",
      "sorular",
      "yeni anket",
      "anket ekle",
      "anket düzenle",
      "anket sil",
      "ankete katıl",
      "anket sonuçları",
      "anket katılımcıları",
      "katılımcı yönetimi",
      "soru yönetimi",
      "bölüm yönetimi",
      "anket listesi",
      "anketlerim",
      "anket oluştur",
      "anket tasarla",
    ],
  },
  {
    id: "meetings",
    title: "Toplantılar",
    icon: Video,
    color: "#7c3aed",
    path: "/meetings",
    keywords: [
      "toplantılar",
      "video",
      "görüşme",
      "toplantı listesi",
      "toplantılarım",
      "yeni toplantı",
      "toplantı ekle",
      "toplantı düzenle",
      "toplantı sil",
      "toplantı katılımcıları",
      "katılımcı yönetimi",
      "toplantı durumu",
      "toplantı filtresi",
      "departman katılımcısı",
      "kullanıcı katılımcısı",
      "toplantı kartı",
      "toplantı seçici",
      "toplantı yönetimi",
      "toplantı planlama",
    ],
  },
  {
    id: "appointments",
    title: "Randevular",
    icon: CalendarClock,
    color: "#4d7c0f",
    path: "/appointments",
    keywords: [
      "randevular",
      "rezervasyon",
      "randevu listesi",
      "randevularım",
      "randevu durumu",
      "randevu filtresi",
      "müşteri görünümü",
      "müşteri seçimi",
      "müşteri sekmeleri",
      "randevu ayarları",
      "haftalık müsaitlik",
      "müsaitlik tablosu",
      "zaman dilimi detayı",
      "randevu yönetimi",
      "randevu planlama",
      "müsaitlik yönetimi",
    ],
  },
  {
    id: "tasks",
    title: "Görevler",
    icon: ClipboardList,
    color: "#475569",
    path: "/tasks",
    keywords: [
      "görevler",
      "işler",
      "projeler",
      "görev listesi",
      "görevlerim",
      "görev kartı",
      "yeni görev",
      "görev ekle",
      "görev oluştur",
      "görev gönder",
      "görev teslimi",
      "görev teslim kartı",
      "kullanıcı görev kartı",
      "görev görüntüle",
      "teslim görüntüle",
      "görev yönetimi",
      "görev takibi",
      "görev durumu",
      "görev filtresi",
    ],
  },
  {
    id: "todos",
    title: "Yapılacaklar",
    icon: ListTodo,
    color: "#78350f",
    path: "/todos",
    keywords: [
      "yapılacaklar",
      "liste",
      "notlar",
      "yapılacak listesi",
      "yapılacaklarım",
      "yapılacak kategorisi",
      "kategori",
      "yapılacak öğesi",
      "öğe",
      "yapılacak etiketi",
      "kategori ekle",
      "yeni kategori",
      "kategori düzenle",
      "kategori sil",
      "öğe ekle",
      "yeni öğe",
      "öğe düzenle",
      "öğe sil",
      "etiketi ekle",
      "etiket düzenle",
      "yapılacak yönetimi",
      "yapılacak takibi",
    ],
  },
  {
    id: "planning",
    title: "Planlama",
    icon: CalendarDays,
    color: "#15803d",
    path: "/planning",
    keywords: [
      "planlama",
      "proje",
      "süreç",
      "program",
      "planlama listesi",
      "planlama yönetimi",
      "sınıf listesi",
      "sınıflar",
      "derslik listesi",
      "derslikler",
      "öğretmen listesi",
      "öğretmenler",
      "öğrenci listesi",
      "öğrenciler",
      "ders listesi",
      "günlük dersler",
      "haftalık müsaitlik",
      "öğretmen ders atamaları",
      "derslik ders atamaları",
      "sınıf ders atamaları",
      "paylaşılan dersler",
      "öğretmen programı",
      "ders programı",
    ],
  },
  {
    id: "files",
    title: "Dosyalar",
    icon: FolderOpen,
    color: "#0f172a",
    path: "/files",
    keywords: [
      "dosyalar",
      "dosya",
      "pdf",
      "dosya yükle",
      "dosya listesi",
      "tüm dosyalar",
      "dosya önizleme",
      "dosya sil",
      "dosya indir",
      "dosya kopyala",
      "depolama",
      "depolama alanı",
      "dosya boyutu",
      "yükleme tarihi",
    ],
  },
  {
    id: "practice-exams",
    title: "Denemeler",
    icon: FileSpreadsheet,
    color: "#0369a1",
    path: "/practice-exams",
    keywords: [
      "denemeler",
      "deneme",
      "deneme sınavı",
      "yeni deneme",
      "deneme oluştur",
      "deneme ekle",
      "deneme listesi",
      "tüm denemeler",
      "cevap anahtarı",
      "sonuç yükle",
      "sonuç görüntüle",
      "deneme sonuçları",
      "deneme tarihi",
      "deneme adı",
      "excel yükle",
    ],
  },
  {
    id: "maarif-test-generator",
    title: "Test Oluşturma",
    icon: Sparkles,
    color: "#1d4ed8",
    path: "/maarif-test-generator",
    keywords: [
      "maarif test",
      "tymmy",
      "tymm",
      "soru üret",
      "test oluştur",
      "gemini",
      "görselli soru",
      "pdf ile test",
      "konudan test",
      "yapay zeka test",
      "maarif modeli",
      "soru oluşturucu",
    ],
  },
  {
    id: "maarif-question-wizard",
    title: "Maarif Soru Sihirbazı",
    icon: Sparkles,
    color: "#059669",
    path: "/maarif-question-wizard",
    keywords: [
      "maarif soru sihirbazı",
      "tek soru",
      "bağlam üretici",
      "soru üretici",
      "soru denetmeni",
      "kazanım sorusu",
      "süreç bileşeni",
      "maarıf soru",
      "tymm soru",
      "soru denetim raporu",
    ],
  },
  {
    id: "whiteboard",
    title: "Beyaz Tahta",
    icon: PenTool,
    color: "#059669",
    path: "/whiteboard",
    keywords: [
      "beyaz tahta",
      "tahta",
      "çizim",
      "kalem",
      "silgi",
      "geri al",
      "ileri al",
      "temizle",
      "indir",
      "kalem boyutu",
      "silgi boyutu",
      "renk seçimi",
      "çizim araçları",
      "yeni tahta",
      "tahta sil",
    ],
  },
  {
    id: "time-center",
    title: "Zaman Merkezi",
    icon: Hourglass,
    color: "#2563eb",
    path: "/time-center",
    keywords: [
      "zaman merkezi",
      "zamanlayıcı",
      "kronometre",
      "pomodoro",
      "oturumlar",
      "odak",
      "istatistik",
      "liderlik tablosu",
      "aktivite",
      "süre takibi",
    ],
  },
  {
    id: "social",
    title: "Sosyal Medya",
    icon: PanelsTopLeft,
    color: "#0f766e",
    path: "/social",
    keywords: [
      "sosyal medya",
      "marka yönetimi",
      "api ayarları",
      "görsel oluştur",
      "video oluştur",
      "post editörü",
      "openai",
      "gemini",
      "marka içerik",
    ],
  },
  {
    id: "ai-assistant",
    title: "AI Asistan",
    icon: Bot,
    color: "#6366f1",
    path: "#ai-panel",
    keywords: [
      "ai",
      "asistan",
      "yapay zeka",
      "yardım",
      "ai panel",
      "yapay zeka paneli",
      "ai asistan paneli",
      "chatbot",
      "sohbet botu",
      "akıllı asistan",
      "ai destek",
      "yapay zeka desteği",
      "ai yardım",
    ],
  },
  {
    id: "notifications",
    title: "Bildirimler",
    icon: Bell,
    color: "#ef4444",
    path: "#notifications",
    keywords: ["bildirimler", "uyarılar", "bildirim paneli", "bildirim merkezi", "uyarı sistemi", "bildirim ayarları"],
  },
  {
    id: "settings",
    title: "Ayarlar",
    icon: Settings,
    color: "#64748b",
    path: "#settings",
    keywords: [
      "ayarlar",
      "tercihler",
      "ayar paneli",
      "ayar merkezi",
      "sistem ayarları",
      "kullanıcı ayarları",
      "genel ayarlar",
      "uygulama ayarları",
    ],
  },
  {
    id: "profile",
    title: "Profil",
    icon: User,
    color: "#8b5cf6",
    path: "#profile",
    keywords: [
      "profil",
      "hesap",
      "kullanıcı bilgileri",
      "profil paneli",
      "hesap bilgileri",
      "kullanıcı profili",
      "kişisel bilgiler",
      "profil düzenle",
      "hesap düzenle",
    ],
  },
];

const TopNavbar = ({
  toggleSidebar,
  toggleAIPanel,
  isEditMode,
  toggleEditMode,
  cancelEditMode,
}: {
  toggleSidebar: () => void;
  toggleAIPanel: () => void;
  isEditMode: boolean;
  toggleEditMode: () => void;
  cancelEditMode: () => void;
}) => {
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [focusSession, setFocusSession] = useState(() => readActiveFocusSessionSummary());
  const [, setFocusTick] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedSearchIndex, setSelectedSearchIndex] = useState(0);
  const [showAIBubble, setShowAIBubble] = useState(() => {
    const bubbleDismissed = localStorage.getItem("aiBubbleDismissed");
    return bubbleDismissed !== "true";
  });

  const [unreadCount, setUnreadCount] = useState(0);

  const searchRef = useRef<HTMLDivElement>(null);

  const dismissAIBubble = () => {
    setShowAIBubble(false);
    localStorage.setItem("aiBubbleDismissed", "true");
  };

  const { user, systemConfig } = useAuth();
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboard = location.pathname === "/" || location.pathname === "/dashboard";
  const canViewTimeCenter = checkPermission("time_tracking_list");
  const liveFocusSession = getLiveFocusSessionSnapshot(focusSession);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchOpen(false);
        setSelectedSearchIndex(0);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleFocusSessionChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      setFocusSession(normalizeActiveFocusSessionSummary(customEvent.detail) || null);
    };
    window.addEventListener(ACTIVE_FOCUS_SESSION_EVENT, handleFocusSessionChange);
    return () => window.removeEventListener(ACTIVE_FOCUS_SESSION_EVENT, handleFocusSessionChange);
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setFocusTick((value) => value + 1);
    }, 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!user?.id || !canViewTimeCenter) {
      clearActiveFocusSessionSummary();
      setFocusSession(null);
      return;
    }

    let mounted = true;

    const loadActiveSession = async () => {
      const response = await ApiService.getMyFocusSessions({ status: "active", page: 1, limit: 1 }).catch(() => null);
      if (!mounted) return;
      const normalized = normalizeActiveFocusSessionSummary(response?.data?.[0] || null);
      setFocusSession(normalized);
      if (normalized) {
        storeActiveFocusSessionSummary(normalized);
        return;
      }
      clearActiveFocusSessionSummary();
    };

    void loadActiveSession();

    return () => {
      mounted = false;
    };
  }, [user?.id, canViewTimeCenter]);

  const openTimeCenter = () => {
    window.dispatchEvent(new CustomEvent("toggleTimerPanel"));
    setShowAIBubble(false);
  };

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await ApiService.getUnreadNotificationCount();
        setUnreadCount(response.unreadCount || 0);
      } catch (error) {
        console.error("Failed to fetch unread count:", error);
      }
    };

    if (user && typeof (user as any).unreadNotifications === 'number') setUnreadCount((user as any).unreadNotifications);
    else fetchUnreadCount();

    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const toggleNotificationsPanel = () => {
    window.dispatchEvent(new CustomEvent("toggleNotificationsPanel"));
    setShowAIBubble(false);
  };

  const permissionMap: Record<string, string | string[]> = {
    calendar: "calendar_list",
    navigator: "navigator_view",
    lessons: "lesson_list",
    surveys: "survey_list",
    employees: ["user_list", "department_list"],
    meetings: "meet_list",
    appointments: "appointment_list",
    tasks: "task_list",
    todos: "todo_list",
    planning: "setting_update",
    announcements: "announcement_list",
    files: "file_list",
    "practice-exams": ["practice_exam_list", "practice_exam_student_list"],
    "maarif-test-generator": "maarif_test_list",
    "maarif-question-wizard": "maarif_question_list",
    "question-bank-indesign": "question_bank_list",
    social: "social_list",
    whiteboard: "whiteboard_view",
    "time-center": "time_tracking_list",
  };

  const hasAccessForItem = (id: string) => {
    const req = permissionMap[id];
    if (!req) return true;
    if (typeof req === "string") return checkPermission(req);

    return req.some((p) => checkPermission(p));
  };

  const getSearchResults = () => {
    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase().trim();
    return searchableItems
      .filter((item) => hasAccessForItem(item.id))
      .filter((item) => item.title.toLowerCase().includes(query) || item.keywords.some((keyword) => keyword.toLowerCase().includes(query)))
      .slice(0, 8);
  };

  const handleSearchFocus = () => {
    setIsSearchOpen(true);
    setSelectedSearchIndex(0);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setSelectedSearchIndex(0);
    setIsSearchOpen(true);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    const results = getSearchResults();

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedSearchIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedSearchIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results.length > 0) {
        handleSearchSelect(results[selectedSearchIndex]);
      }
    } else if (e.key === "Escape") {
      setIsSearchOpen(false);
      setSearchQuery("");
      setSelectedSearchIndex(0);
    }
  };

  const handleSearchSelect = (item: (typeof searchableItems)[0]) => {
    if (!hasAccessForItem(item.id)) {
      return;
    }
    setIsSearchOpen(false);
    setSearchQuery("");
    setSelectedSearchIndex(0);

    if (item.path === "#ai-panel") {
      toggleAIPanel();
    } else if (item.path === "#settings") {
      toggleSettingsPanel();
    } else if (item.path === "#notifications") {
      toggleNotificationsPanel();
    } else if (item.path === "#profile") {
      window.dispatchEvent(new CustomEvent("toggleUserProfile"));
    } else if (item.path.startsWith("/")) {
      navigate(item.path);
    }
  };

  const toggleSettingsPanel = () => {
    window.dispatchEvent(new CustomEvent("toggleSettingsPanel"));
  };

  const toggleHelpPanel = () => {
    window.dispatchEvent(new CustomEvent("toggleHelpPanel"));
  };

  return (
    <>
      <div
        className="fixed top-0 left-0 right-0 h-[60px] flex items-center justify-between pr-2 sm:pr-4 z-[999] backdrop-blur-md border-b border-white/15"
        style={{
          backgroundColor: `${currentTheme.colors.background.base}e6`,
        }}>
        <div className="flex items-center">
          <button
            onClick={() => {
              toggleSidebar();
              setShowAIBubble(false);
            }}
            className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-white ml-2 mr-2 sm:mr-4 transition-all duration-200"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}33`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}>
            <div className="grid grid-cols-3 gap-[2px]">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="w-[6px] h-[6px] bg-white rounded-sm"></div>
              ))}
            </div>
          </button>

          <div
            className="flex items-center mr-2 sm:mr-6 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate("/dashboard")}>
            <img
              src={systemConfig?.companyLogo || "/logo.svg"}
              alt={systemConfig?.companyName || "Yeni Eğitim Sistemi"}
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover shadow-md"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                if (target.src.includes("favicon")) return;
                target.src = "/favicon/favicon-96x96.png";
              }}
            />
            <div className="ml-2 text-white leading-tight">
              <div className="text-sm sm:text-lg font-semibold drop-shadow-lg md:drop-shadow-none">
                {systemConfig?.companyName || "Yeni Eğitim Sistemi"}
              </div>
              <div className="text-[8px] sm:text-[10px] text-gray-400 font-semibold tracking-wide drop-shadow-lg md:drop-shadow-none"></div>
            </div>
          </div>

          <div className="hidden sm:flex items-center space-x-2">
            <button
              className="w-10 h-10 flex items-center justify-center text-white ml-2 rounded-full transition-all duration-300 hover:scale-110 touch-manipulation opacity-50 cursor-not-allowed"
              style={{
                backgroundColor: `${currentTheme.colors.primary}33`,
                border: `1px solid ${currentTheme.colors.primary}66`,
              }}
              disabled
              onClick={(e) => e.preventDefault()}>
              <Plus size={18} />
            </button>
            {!isEditMode ? (
              <button
                onClick={toggleEditMode}
                className="w-10 h-10 flex items-center justify-center text-white ml-2 rounded-full transition-all duration-300 hover:scale-110 touch-manipulation"
                style={{
                  backgroundColor: `${currentTheme.colors.primary}33`,
                  border: `1px solid ${currentTheme.colors.primary}66`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}4d`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}33`;
                }}
                title="Sıralamayı Düzenle">
                <Edit size={18} />
              </button>
            ) : (
              <>
                <button
                  onClick={toggleEditMode}
                  className="w-10 h-10 flex items-center justify-center text-white ml-2 rounded-full transition-all duration-300 hover:scale-110 touch-manipulation"
                  style={{
                    backgroundColor: `${currentTheme.colors.secondary}33`,
                    border: `1px solid ${currentTheme.colors.secondary}66`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${currentTheme.colors.secondary}4d`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = `${currentTheme.colors.secondary}33`;
                  }}
                  title="Sıralamayı Kaydet">
                  <Save size={18} />
                </button>
                <button
                  onClick={cancelEditMode}
                  className="w-10 h-10 flex items-center justify-center text-white ml-2 rounded-full transition-all duration-300 hover:scale-110 touch-manipulation"
                  style={{
                    backgroundColor: `#ef444433`,
                    border: `1px solid #ef444466`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `#ef44444d`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = `#ef444433`;
                  }}
                  title="İptal">
                  <X size={18} />
                </button>
              </>
            )}
          </div>
        </div>

        <div className="flex-1 max-w-3xl mx-2 sm:mx-8 hidden md:block" ref={searchRef}>
          <div className="relative">
            <input
              type="text"
              placeholder="Sayfalar, özellikler ve daha fazlasını ara..."
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={(e) => {
                handleSearchFocus();
                e.currentTarget.style.backgroundColor = `${currentTheme.colors.background.via}cc`;
                e.currentTarget.style.borderColor = `${currentTheme.colors.primary}66`;
              }}
              onKeyDown={handleSearchKeyDown}
              className="w-full text-white py-3 pl-10 pr-10 rounded-xl focus:outline-none placeholder-gray-400 transition-all duration-300"
              style={
                {
                  backgroundColor: `${currentTheme.colors.background.base}cc`,
                  border: `1px solid ${currentTheme.colors.primary}33`,
                } as React.CSSProperties
              }
              onBlur={(e) => {
                e.currentTarget.style.backgroundColor = `${currentTheme.colors.background.base}cc`;
                e.currentTarget.style.borderColor = `${currentTheme.colors.primary}33`;
              }}
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />

            {isSearchOpen && searchQuery.trim() && (
              <div
                className="absolute top-full left-0 right-0 mt-2 rounded-xl shadow-2xl z-50 max-h-96 overflow-y-auto custom-scrollbar transition-all duration-300"
                style={{
                  background: `linear-gradient(to bottom right, ${currentTheme.colors.background.base}f5, ${currentTheme.colors.background.via}f5, ${currentTheme.colors.background.base}f5)`,
                  border: `1px solid ${currentTheme.colors.primary}4d`,
                  backdropFilter: "blur(12px)",
                }}>
                {getSearchResults().length > 0 ? (
                  <>
                    <div
                      className="px-4 py-3 border-b transition-all duration-300"
                      style={{
                        borderColor: `${currentTheme.colors.primary}4d`,
                      }}>
                      <div
                        className="text-sm font-medium flex items-center transition-colors duration-300"
                        style={{ color: currentTheme.colors.accent }}>
                        <Search size={16} className="mr-2" />
                        Arama Sonuçları ({getSearchResults().length})
                      </div>
                    </div>
                    <div className="py-2">
                      {getSearchResults().map((item, index) => (
                        <div
                          key={item.id}
                          onClick={() => handleSearchSelect(item)}
                          className="flex items-center px-4 py-3 cursor-pointer transition-all duration-200"
                          style={
                            index === selectedSearchIndex
                              ? {
                                backgroundColor: `${currentTheme.colors.primary}4d`,
                                borderLeft: `4px solid ${currentTheme.colors.primary}`,
                              }
                              : {}
                          }
                          onMouseEnter={(e) => {
                            if (index !== selectedSearchIndex) {
                              e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}33`;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (index !== selectedSearchIndex) {
                              e.currentTarget.style.backgroundColor = "transparent";
                            }
                          }}>
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center mr-3 shadow-lg"
                            style={{
                              backgroundColor: item.color,
                              boxShadow: `0 4px 15px ${item.color}40`,
                            }}>
                            <item.icon size={20} className="text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-medium text-sm truncate">{item.title}</h3>
                          </div>
                          <ChevronRight
                            size={16}
                            className={`text-gray-400 ml-2 transition-transform ${index === selectedSearchIndex ? "rotate-90" : ""}`}
                          />
                        </div>
                      ))}
                    </div>
                    <div
                      className="px-4 py-3 border-t transition-all duration-300"
                      style={{
                        borderColor: `${currentTheme.colors.primary}4d`,
                        backgroundImage: `linear-gradient(to right, ${currentTheme.colors.primary}1a, ${currentTheme.colors.secondary}1a)`,
                      }}>
                      <div
                        className="text-xs flex items-center justify-between transition-colors duration-300"
                        style={{ color: currentTheme.colors.accent }}>
                        <span>⌨️ Klavye ile dolaşın</span>
                        <span>Enter: Seç • Esc: Kapat</span>
                      </div>
                    </div>
                  </>
                ) : searchQuery.trim() ? (
                  <div className="p-6 text-center">
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300"
                      style={{
                        backgroundColor: `${currentTheme.colors.primary}33`,
                      }}>
                      <Search size={32} style={{ color: currentTheme.colors.primary }} />
                    </div>
                    <p className="text-sm transition-colors duration-300" style={{ color: currentTheme.colors.accent }}>
                      "{searchQuery}" için sonuç bulunamadı
                    </p>
                    <p className="text-xs mt-2 transition-colors duration-300" style={{ color: currentTheme.colors.primary }}>
                      Farklı anahtar kelimeler deneyin
                    </p>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-1 sm:space-x-3">
          <button
            onClick={() => setIsMobileSearchOpen(true)}
            className="w-8 h-8 sm:w-9 sm:h-9 flex md:hidden items-center justify-center text-white rounded transition-all duration-200 touch-manipulation"
            aria-label="Ara"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}33`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}>
            <Search size={16} />
          </button>

          <div className="relative">
            <button
              onClick={() => {
                toggleAIPanel();
                setShowAIBubble(false);
              }}
              className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-white border border-white text-xs font-bold rounded hover:bg-white/20 transition-colors touch-manipulation">
              AI
            </button>
            {showAIBubble && isDashboard && (
              <div
                className="absolute top-full mt-2 left-0 whitespace-nowrap z-50 cursor-pointer"
                onClick={() => {
                  toggleAIPanel();
                  dismissAIBubble();
                }}>
                <div
                  className="relative text-white text-xs font-medium px-3 py-2 rounded-lg shadow-lg backdrop-blur-sm"
                  style={{
                    background: `linear-gradient(135deg, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary})`,
                    border: `1px solid ${currentTheme.colors.accent}66`,
                  }}>
                  <div className="flex items-center gap-2">
                    <Bot size={14} className="text-white" />
                    <div>
                      <div className="font-semibold">Merhaba {user?.firstName || "Kullanıcı"}!</div>
                      <div className="text-[10px] opacity-90">Deep asistanınız burada.</div>
                    </div>
                  </div>
                  <div
                    className="absolute -top-2 left-3 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[8px]"
                    style={{ borderBottomColor: currentTheme.colors.primary }}></div>
                </div>
              </div>
            )}
          </div>

          {canViewTimeCenter ? (
            <div className="relative">
              <button
                onClick={openTimeCenter}
                className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-white rounded transition-all duration-200 touch-manipulation"
                title="Zaman Merkezi"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}33`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}>
                <Hourglass size={16} />
              </button>
              {liveFocusSession && (() => {
                const baseValue =
                  liveFocusSession.type === "stopwatch"
                    ? liveFocusSession.liveElapsedSeconds
                    : liveFocusSession.liveRemainingSeconds || 0;
                const prefix =
                  liveFocusSession.type === "stopwatch"
                    ? "KR "
                    : liveFocusSession.type === "pomodoro"
                      ? "PO "
                      : "";
                const label = `${prefix}${formatSeconds(baseValue)}${liveFocusSession.status === "paused" ? " • D" : ""}`;
                return (
                  <div
                    className="absolute top-full mt-1 left-1/2 -translate-x-1/2 whitespace-nowrap z-50 cursor-pointer"
                    onClick={openTimeCenter}>
                    <div
                      className="text-white text-[11px] font-bold px-2 py-0.5 rounded-md shadow-lg"
                      style={{
                        background: `linear-gradient(135deg, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary})`,
                        border: `1px solid ${currentTheme.colors.accent}66`,
                        fontVariantNumeric: "tabular-nums",
                      }}>
                      {label}
                    </div>
                    <div
                      className="absolute -top-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-b-[6px]"
                      style={{ borderBottomColor: currentTheme.colors.primary }}
                    />
                  </div>
                );
              })()}
            </div>
          ) : null}

          <div className="relative">
            <button
              onClick={toggleNotificationsPanel}
              className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-white rounded transition-all duration-200 touch-manipulation relative"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}33`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}>
              <Bell size={16} />
              {unreadCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white rounded-full px-1"
                  style={{
                    backgroundColor: "#ef4444",
                    boxShadow: "0 2px 4px rgba(239, 68, 68, 0.4)",
                  }}>
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
          </div>
          <button
            onClick={() => {
              toggleHelpPanel();
              setShowAIBubble(false);
            }}
            className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-white rounded transition-all duration-200 touch-manipulation"
            title="Yardım ve Dokümantasyon"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}33`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}>
            <HelpCircle size={16} />
          </button>
          <button
            onClick={() => {
              toggleSettingsPanel();
              setShowAIBubble(false);
            }}
            className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-white rounded transition-all duration-200 touch-manipulation"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}33`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}>
            <Settings size={16} />
          </button>
          <div className="relative">
            <button
              onClick={() => {
                window.dispatchEvent(new CustomEvent("toggleUserProfile"));
                setShowAIBubble(false);
              }}
              className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-white rounded transition-all duration-200 touch-manipulation"
              title="Profili Düzenle"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}33`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}>
              <User size={16} />
            </button>
          </div>
        </div>
      </div>

      {isMobileSearchOpen && (
        <div className="md:hidden fixed inset-0 z-50" role="dialog" aria-modal="true">
          <div
            className="absolute top-[60px] left-0 right-0 bottom-0"
            style={{
              backgroundColor: `${currentTheme.colors.background.base}f2`,
            }}>
            <div
              className="flex items-center p-3 border-b transition-all duration-300"
              style={{ borderColor: `${currentTheme.colors.primary}4d` }}>
              <button
                onClick={() => setIsMobileSearchOpen(false)}
                className="w-10 h-10 flex items-center justify-center text-white rounded transition-all duration-200 touch-manipulation"
                aria-label="Kapat"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}33`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}>
                <X size={20} />
              </button>
              <div className="flex-1 relative ml-2" ref={searchRef}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                <input
                  type="text"
                  placeholder="Ara..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  className="w-full text-white py-3 pl-10 pr-4 rounded-xl focus:outline-none placeholder-gray-300 transition-all duration-300"
                  style={
                    {
                      backgroundColor: `${currentTheme.colors.background.base}cc`,
                      border: `1px solid ${currentTheme.colors.primary}33`,
                    } as React.CSSProperties
                  }
                  onFocus={(e) => {
                    e.currentTarget.style.backgroundColor = `${currentTheme.colors.background.via}cc`;
                    e.currentTarget.style.borderColor = `${currentTheme.colors.primary}66`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.backgroundColor = `${currentTheme.colors.background.base}cc`;
                    e.currentTarget.style.borderColor = `${currentTheme.colors.primary}33`;
                  }}
                  autoFocus
                />
              </div>
            </div>

            <div className="p-3 overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
              {getSearchResults().length > 0 ? (
                getSearchResults().map((item) => (
                  <div
                    key={item.id}
                    onClick={() => {
                      handleSearchSelect(item);
                      setIsMobileSearchOpen(false);
                    }}
                    className="flex items-center p-3 rounded-lg cursor-pointer transition-all duration-200"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}33`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center mr-3" style={{ backgroundColor: item.color }}>
                      <item.icon size={18} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-medium truncate">{item.title}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-sm transition-colors duration-300" style={{ color: currentTheme.colors.accent }}>
                  Aramak için yazmaya başlayın...
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TopNavbar;
